import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, SaveIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Control, Controller, useForm } from "react-hook-form";
import { generatePath, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { Button, DeleteModal, PageBreadcrumb, SelectV2, Spinner } from "@/modules/common/components";
import { ManagementCompanyStatus, ManagementCompanyUsageType, ManagementModel } from "@/modules/common/types/ManagementCompanies.enum";
import { formatCnpj } from "@/modules/common/utils/format";
import useNavigateBack from "@/modules/common/hooks/useNavigateBack";
import { ROUTES } from "@/routes/constants/routes";
import useManagerCompanyDetails from "../manager-details/hooks/useManagerCompanyDetails";
import ManagementCompanyFormFields from "../../components/ManagementCompanyFormFields";
import useUpdateManagementCompany from "../../hooks/useUpdateManagementCompany";
import {
  INACTIVATE_COMPANY_CONFIRM_TEXT,
  INACTIVATE_COMPANY_WARNING,
  buildInactivateCompanyDescription,
} from "../../constants/InactivateManagementCompany";
import { ManagementCompanyBaseFormData, UpdateManagementCompanyFormData, digitsOnly, updateManagementCompanySchema } from "../../schemas";
import useManagementCompanyManagerUsers from "@/modules/common/hooks/lists/useManagementCompanyManagerUsers";
import { useSessionStore } from "@/modules/auth/store/useSessionStore";
import { AdminUserRole } from "@/modules/common/types/Users.enum";

const ManagerEdit = () => {
  const { id } = useParams();
  const { canUserSee } = useSessionStore();
  const navigateBack = useNavigateBack(ROUTES.MANAGER.LIST);

  const { data: company, isLoading, isError, error } = useManagerCompanyDetails({ id: id!, enabled: !!id });
  const updateManagementCompany = useUpdateManagementCompany();
  const {
    options: ownerOptions,
    isLoading: isLoadingOwnerOptions,
    hasMore: hasMoreOwnerOptions,
    onLoadMore: fetchMoreOwnerOptions,
    onSearch: searchOwnerOptions,
  } = useManagementCompanyManagerUsers({
    enabled: !!id,
    managementCompanyId: id!,
    currentValue: company?.owner ? { value: company.owner.id, label: company.owner.name } : undefined,
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UpdateManagementCompanyFormData>({
    resolver: zodResolver(updateManagementCompanySchema),
  });

  useEffect(() => {
    if (isError) {
      toast.error(error?.message || "Erro ao carregar gestora.");
      navigateBack();
    }
  }, [isError, error]);

  useEffect(() => {
    if (!company) return;

    reset({
      name: company.name,
      corporateName: company.corporateName ?? "",
      cnpj: company.cnpj ? formatCnpj(company.cnpj) : "",
      email: company.email ?? "",
      managementModel: company.managementModel !== null ? String(company.managementModel) : "",
      status: String(company.status),
      usageType: company.usageType,
      ownerId: company.owner?.id ?? "",
    });
  }, [company, reset]);

  const isDemoManager = company?.usageType === ManagementCompanyUsageType.DEMONSTRATION;

  const [pendingInactivation, setPendingInactivation] = useState<UpdateManagementCompanyFormData | null>(null);

  const saveCompany = async (values: UpdateManagementCompanyFormData) => {
    if (!id) return;

    await updateManagementCompany.mutateAsync({
      id,
      data: {
        name: values.name,
        corporateName: values.corporateName || undefined,
        // An empty CNPJ clears it, which the API only accepts for demonstration gestoras
        cnpj: values.cnpj ? digitsOnly(values.cnpj) : "",
        email: values.email || undefined,
        managementModel: Number(values.managementModel) as ManagementModel,
        status: Number(values.status) as ManagementCompanyStatus,
        ownerId: values.ownerId,
      },
    });
  };

  const onSubmit = async (values: UpdateManagementCompanyFormData) => {
    if (!id) return;

    const isInactivating =
      Number(values.status) === ManagementCompanyStatus.INACTIVE && company?.status !== ManagementCompanyStatus.INACTIVE;

    if (isInactivating) {
      setPendingInactivation(values);
      return;
    }

    await saveCompany(values);
  };

  const handleConfirmInactivation = async () => {
    if (!pendingInactivation) return;

    try {
      await saveCompany(pendingInactivation);
      setPendingInactivation(null);
    } catch {}
  };

  return (
    <div className="flex h-full flex-col gap-4">
      <PageBreadcrumb
        items={[
          { label: "Gestoras", to: ROUTES.MANAGER.LIST },
          ...(company && id ? [{ label: company.name, to: generatePath(ROUTES.MANAGER.DETAILS, { id }) }] : []),
          { label: "Editar" },
        ]}
      />

      {isLoading && company === undefined ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : !!company ? (
        <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col">
              <p className="text-[22px] font-bold text-ui-primary">Editar Gestora</p>
              <p className="text-[14px] text-ui-text-muted">Atualize os dados cadastrais de {company.name}.</p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                variant="outlined"
                label="Voltar"
                icon={<ArrowLeft />}
                className="w-full sm:w-auto"
                onClick={navigateBack}
              />
              {canUserSee(AdminUserRole.ADMIN) && (
                <Button
                  type="submit"
                  label="Salvar Alterações"
                  icon={<SaveIcon />}
                  loading={updateManagementCompany.isPending}
                  disabled={updateManagementCompany.isPending}
                  className="w-full sm:w-auto"
                />
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4 rounded-xl bg-white p-4">
            <div className="flex flex-col">
              <p className="text-[16px] font-bold text-ui-primary">Dados Gerais</p>
              <p className="text-[12px] text-ui-text-muted">Informações cadastrais da gestora.</p>
            </div>

            <ManagementCompanyFormFields
              register={register}
              control={control as unknown as Control<ManagementCompanyBaseFormData>}
              errors={errors}
              requireCnpj={!isDemoManager}
            />
          </div>

          <div className="flex flex-col gap-4 rounded-xl bg-white p-4">
            <div className="flex flex-col">
              <p className="text-[16px] font-bold text-ui-primary">Representante Principal</p>
              <p className="text-[12px] text-ui-text-muted">Responsável pelo acesso e gestão inicial da gestora.</p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Controller
                  name="ownerId"
                  control={control}
                  render={({ field }) => (
                    <SelectV2
                      mode="single"
                      label="Representante"
                      markAsRequired
                      placeholder="Selecione o representante"
                      options={ownerOptions}
                      isLoading={isLoadingOwnerOptions}
                      hasMore={hasMoreOwnerOptions}
                      onLoadMore={fetchMoreOwnerOptions}
                      onSearch={searchOwnerOptions}
                      value={field.value || undefined}
                      onChange={(next) => field.onChange(next ?? "")}
                    />
                  )}
                />
                {errors.ownerId && <p className="mt-1 text-xs text-red-500">{errors.ownerId.message}</p>}
              </div>
            </div>
          </div>
        </form>
      ) : null}

      <DeleteModal
        open={!!pendingInactivation}
        title="Inativar gestora"
        description={buildInactivateCompanyDescription(company?.name)}
        warning={INACTIVATE_COMPANY_WARNING}
        confirmText={INACTIVATE_COMPANY_CONFIRM_TEXT}
        loading={updateManagementCompany.isPending}
        onDelete={handleConfirmInactivation}
        onClose={() => setPendingInactivation(null)}
      />
    </div>
  );
};

export default ManagerEdit;
